import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'wouter'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { skipToken, useSuspenseQuery } from '@apollo/client'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  Delete01Icon,
  FloppyDiskIcon,
  Megaphone01Icon,
  PlusSignIcon
} from '@hugeicons/core-solid-rounded'

import { useCloseModal } from '@/Router'
import * as Form from '@/features/form'
import { useBranding } from '@/features/branding'
import Button from '@/components/ui/Button'
import { GalleryUpload } from '@/features/files'
import { Permission, useCheckPermission } from '@/features/auth'
import { Editor } from '@/features/editor'
import Input from '@/components/ui/Input'
import { Seperator } from '@/components/ui/Seperator'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { useTypeToLabel } from '../utils/helpers'

import useGoodieForm, {
  FormData,
  TRANSLATABLE_FIELDS
} from '../hooks/useGoodieForm'

// Fields
import MessageFields from './fields/MessageFields'
import DefaultFields from './fields/DefaultFields'
import EventFields from './fields/EventFields'
import { generateDefaults } from '../utils/generateFormDefaults'

interface Props {
  id?: number
  type?: GraphQL.GoodieType
}

export default function GoodieForm(props: Props) {
  const { type, id } = props

  const canCreateAndEditGoodie = useCheckPermission([
    Permission.GOODIE_CREATE,
    Permission.GOODIE_UPDATE
  ])

  const canCreatePromotion = useCheckPermission([Permission.PROMOTION_CREATE])

  const { data } = useSuspenseQuery(
    AdminAPI.goodie.GET,
    id
      ? {
          variables: { id, withGallery: true, withTranslations: true }
        }
      : skipToken
  )

  const goodie = useMemo(
    () => getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, data?.getGoodie),
    [data]
  )

  const finalType = useMemo(
    () => goodie?.type ?? type ?? GraphQL.GoodieType.Default,
    [goodie, type]
  )

  return (
    <FormContent
      goodie={goodie}
      canCreatePromotion={canCreatePromotion}
      readOnly={!canCreateAndEditGoodie}
      currentType={finalType}
    />
  )
}

interface ContentProps {
  goodie?: GraphQL.GoodieFragmentFragment
  currentType: GraphQL.GoodieType
  canCreatePromotion: boolean
  readOnly: boolean
}

function FormContent({
  goodie,
  currentType,
  canCreatePromotion,
  readOnly
}: ContentProps) {
  const { t } = useLingui()
  const [, navigate] = useLocation()
  const closeModal = useCloseModal()
  const formRef = useRef<Form.RefType<FormData>>(null)
  const shouldCreatePromotion = useRef(false)
  const shouldCreateAnotherGoodie = useRef(false)
  const submitRef = useRef<HTMLButtonElement>(null)
  const goodieTypeToLabel = useTypeToLabel()

  const {
    branding: { supportedLocales }
  } = useBranding()

  const defaultValues = useRef(
    generateDefaults({
      type: currentType,
      goodie,
      supportedLocales: supportedLocales ?? []
    })
  )

  const resetToDefault = useCallback(
    ({ keepDirtyValues = false }: { keepDirtyValues?: boolean } = {}) => {
      if (!formRef.current) return

      formRef.current.reset(defaultValues.current, {
        keepDirtyValues
      })
    },
    []
  )

  useEffect(() => {
    if (!formRef.current) return

    defaultValues.current = generateDefaults({
      type: currentType,
      goodie,
      supportedLocales: supportedLocales ?? []
    })

    resetToDefault({ keepDirtyValues: true })

    // For messages we remove all files but 1
    if (currentType === GraphQL.GoodieType.Message) {
      const values = formRef.current.getValues()

      formRef.current.setValue('files', values.files.slice(0, 1))
    }
  }, [goodie, currentType, resetToDefault, supportedLocales])

  //#endregion

  const onDone = useCallback(
    (newGoodie?: GraphQL.GoodieFragmentFragment, deleted?: boolean) => {
      if (deleted) {
        closeModal()

        return
      }

      if (goodie) return
      if (!newGoodie) return

      if (!!shouldCreatePromotion.current && !!canCreatePromotion) {
        navigate(`/create-promotion/${newGoodie.token}`)

        return
      }

      // This needs to run in the submitted successfully hook, so that use-react-form is ready
      if (shouldCreateAnotherGoodie.current) return

      closeModal()
    },
    [goodie, canCreatePromotion, shouldCreatePromotion, navigate, closeModal]
  )

  const onSubmittedSuccessfully = useCallback(() => {
    if (!shouldCreateAnotherGoodie.current) return

    shouldCreateAnotherGoodie.current = false
    resetToDefault()
  }, [resetToDefault])

  const { errors, create, update, remove, progress } = useGoodieForm({
    id: goodie?.id,
    onDone
  })

  const createWithPromotion = useCallback(() => {
    shouldCreatePromotion.current = true
    submitRef.current?.click()
  }, [])

  const createAndNew = useCallback(() => {
    shouldCreateAnotherGoodie.current = true
    submitRef.current?.click()
  }, [])

  const createPromotion = useCallback(() => {
    if (goodie) {
      navigate(`/create-promotion/${goodie.token}`)
    }
  }, [goodie, navigate])

  const goodieType = goodieTypeToLabel[currentType]
  const name = goodie?.name

  return (
    <Form.Root<FormData>
      onSubmit={goodie ? update : create}
      serverErrors={errors}
      readOnly={readOnly}
      defaultValues={defaultValues.current}
      ref={formRef}
      onSubmittedSuccessfully={onSubmittedSuccessfully}
      generateDefaultValues={() =>
        generateDefaults({
          type: currentType,
          goodie,
          supportedLocales: supportedLocales ?? []
        })
      }
    >
      <Form.Translation translatableFields={TRANSLATABLE_FIELDS}>
        <div className="relative flex w-full flex-col gap-6 p-2.5">
          <Form.Field
            name="name"
            label={`${t`Name (isn't shown to Guests)`}*`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              },
              validate: {
                required: Form.Validations.requiredWithTrimValidation(
                  t`This field is required`
                )
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                onChange={event => {
                  const newName = event.target.value

                  onChange(newName)
                }}
                type="text"
                placeholder={t({
                  comment: 'Placeholder for the Name input on the Goodie',
                  message: 'e.g.: Hand-Massage 90mins'
                })}
                {...props}
              />
            )}
          </Form.Field>
          <Seperator heading={t`Public Information`} />
          <Form.Field
            name="title"
            label={`${t`Title`}*`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              },
              validate: {
                required: Form.Validations.requiredWithTrimValidation(
                  t`This field is required`
                )
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                onChange={event => onChange(event.target.value)}
                type="text"
                placeholder={t({
                  comment: 'Placeholder for the Title Input on the Goodie',
                  message: 'e.g.: Hand-Massage'
                })}
                {...props}
              />
            )}
          </Form.Field>
          <Form.Data<FormData>>
            {({ type }) => {
              if (type === GraphQL.GoodieType.Message) return null

              return (
                <Form.Field name="description" label={t`Description`}>
                  {({ renderProps: { value, ...props } }) => (
                    <Editor
                      placeholder={t`More information for the guest about the promotion`}
                      markdown={value}
                      {...props}
                    />
                  )}
                </Form.Field>
              )
            }}
          </Form.Data>
          <Form.Data<FormData>>
            {({ type }) => {
              // Message type gallery is managed differently, since they only have one message
              if (type === GraphQL.GoodieType.Message) return null

              return (
                <Form.Field<FormData>
                  name="files"
                  label={t`Gallery`}
                  description={t({
                    comment: 'Description of the Gallery Field for the Goodie',
                    message:
                      "Goodies that don't have Images/Videos can't be shown on the Feed."
                  })}
                >
                  {({
                    locale,
                    isDefaultLocale,
                    renderProps: { value, ...props }
                  }) => (
                    <GalleryUpload
                      files={value}
                      accept="image/*,video/*"
                      locale={locale}
                      isDefaultLocale={isDefaultLocale}
                      {...props}
                    />
                  )}
                </Form.Field>
              )
            }}
          </Form.Data>
          <Seperator heading={t`${goodieType} specific`} />
          <Form.Data<FormData>>
            {({ type }) => {
              switch (type) {
                case GraphQL.GoodieType.Default:
                  return <DefaultFields />
                case GraphQL.GoodieType.Event:
                  return <EventFields />
                case GraphQL.GoodieType.Message:
                  return <MessageFields />
              }
            }}
          </Form.Data>
          {!readOnly && (
            <div className="flex flex-col">
              {progress != null && (
                <ProgressBar value={progress} title={t`Upload Files`} />
              )}
              <footer className="flex flex-row items-center justify-between gap-2.5 mt-2.5">
                <div className="flex flex-row justify-start gap-2.5">
                  {goodie && (
                    <ConfirmDialog
                      title={t`Delete Goodie?`}
                      message={t`Are you sure that you want to delete the Goodie "${name}", and all it's promotions, this can not be reversed?`}
                      onConfirm={remove}
                      asChild
                    >
                      <Button
                        type="button"
                        variant="outline"
                        Icon={Delete01Icon}
                      >
                        <Trans>Delete</Trans>
                      </Button>
                    </ConfirmDialog>
                  )}
                </div>
                <div className="flex flex-row items-center justify-end gap-2.5">
                  {canCreatePromotion && goodie && (
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={createPromotion}
                        Icon={Megaphone01Icon}
                      >
                        <Trans>Add to Feed</Trans>
                      </Button>
                    </div>
                  )}
                  {!goodie && (
                    <div>
                      <Form.Submit
                        type="button"
                        variant="outline"
                        Icon={PlusSignIcon}
                        onClick={createAndNew}
                      >
                        <Trans>Save and New</Trans>
                      </Form.Submit>
                    </div>
                  )}
                  {canCreatePromotion && !goodie && (
                    <div>
                      <Form.Submit
                        type="button"
                        variant="outline"
                        onClick={createWithPromotion}
                        Icon={Megaphone01Icon}
                      >
                        <Trans>Save and add to Feed</Trans>
                      </Form.Submit>
                    </div>
                  )}
                  <div>
                    <Form.Submit
                      Icon={FloppyDiskIcon}
                      ref={submitRef}
                      onlyValidWhenDirty={!!goodie}
                    >
                      <Trans>Save</Trans>
                    </Form.Submit>
                  </div>
                </div>
              </footer>
            </div>
          )}
        </div>
      </Form.Translation>
    </Form.Root>
  )
}
